import React, { useEffect, useState, useCallback } from 'react';
import { __ } from '@wordpress/i18n';
import CreditService from '../../../../service/credit-service';
import './ProfileCredits.css';

const KIND_LABEL = {
    topup: 'Ricarica',
    hold: 'Blocco',
    settle: 'Addebito',
    release: 'Rilascio',
    refund: 'Rimborso',
    adjust: 'Rettifica',
};

const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return iso; }
};

const ProfileCredits = () => {
    const [wallet, setWallet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pricing, setPricing] = useState(null); // null = non admin / non caricato
    const [savingKey, setSavingKey] = useState(null);

    const loadWallet = useCallback(async () => {
        setLoading(true);
        try {
            const w = await CreditService.getMyWalletAsync();
            setWallet(w);
        } catch {
            // gestito globalmente
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPricing = useCallback(async () => {
        try {
            const p = await CreditService.getPricingAsync();
            setPricing(p); // null se non admin
        } catch {
            setPricing(null);
        }
    }, []);

    useEffect(() => {
        loadWallet();
        loadPricing();
    }, [loadWallet, loadPricing]);

    const handlePricingChange = (key, field, value) => {
        setPricing(prev => (prev || []).map(row =>
            row.serviceKey === key ? { ...row, [field]: value } : row
        ));
    };

    const handleSaveRow = async (row) => {
        setSavingKey(row.serviceKey);
        try {
            const updated = await CreditService.updatePricingAsync(row.serviceKey, {
                providerCost: parseFloat(row.providerCost),
                markupPct: row.markupPct === '' || row.markupPct === null ? null : parseFloat(row.markupPct),
                active: !!row.active,
            });
            if (updated) {
                setPricing(prev => (prev || []).map(r => r.serviceKey === row.serviceKey ? updated : r));
            }
        } catch {
            // no-op
        } finally {
            setSavingKey(null);
        }
    };

    return (
        <section className="workbenchProfile workbenchProfile--credits">
            <div className="credits__balanceCard">
                <h3 className="workbenchProfile__sectionTitle">{__('I tuoi crediti', 'soundbetween')}</h3>
                {loading ? (
                    <p className="credits__muted">{__('Caricamento…', 'soundbetween')}</p>
                ) : (
                    <>
                        <div className="credits__balanceGrid">
                            <div className="credits__stat credits__stat--big">
                                <span className="credits__statValue">{wallet?.available ?? 0}</span>
                                <span className="credits__statLabel">{__('Disponibili', 'soundbetween')}</span>
                            </div>
                            <div className="credits__stat">
                                <span className="credits__statValue">{wallet?.balance ?? 0}</span>
                                <span className="credits__statLabel">{__('Saldo totale', 'soundbetween')}</span>
                            </div>
                            <div className="credits__stat">
                                <span className="credits__statValue">{wallet?.held ?? 0}</span>
                                <span className="credits__statLabel">{__('Bloccati', 'soundbetween')}</span>
                            </div>
                        </div>
                        {!wallet?.creditsEnabled && (
                            <p className="credits__notice">
                                {__('Il sistema a crediti non è ancora attivo: le funzioni AI al momento non consumano crediti.', 'soundbetween')}
                            </p>
                        )}
                    </>
                )}
            </div>

            <div className="credits__ledger">
                <h3 className="workbenchProfile__sectionTitle">{__('Movimenti', 'soundbetween')}</h3>
                {(!wallet?.transactions || wallet.transactions.length === 0) ? (
                    <p className="credits__muted">{__('Nessun movimento.', 'soundbetween')}</p>
                ) : (
                    <ul className="credits__txList">
                        {wallet.transactions.map(tx => (
                            <li key={tx.id} className="credits__tx">
                                <div className="credits__txMain">
                                    <span className="credits__txKind">{KIND_LABEL[tx.kind] || tx.kind}</span>
                                    {tx.serviceKey && <span className="credits__txSvc">{tx.serviceKey}</span>}
                                </div>
                                <div className="credits__txRight">
                                    <span className={`credits__txAmount ${tx.amount >= 0 ? 'pos' : 'neg'}`}>
                                        {tx.amount >= 0 ? '+' : ''}{tx.amount}
                                    </span>
                                    <span className="credits__txDate">{fmtDate(tx.createdAt)}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {Array.isArray(pricing) && (
                <div className="credits__admin">
                    <h3 className="workbenchProfile__sectionTitle">{__('Listino servizi AI (admin)', 'soundbetween')}</h3>
                    <p className="credits__muted">
                        {__('Costo provider + ricarico per singolo servizio. Attiva un servizio per farlo pagare in crediti.', 'soundbetween')}
                    </p>
                    <div className="credits__tableWrap">
                        <table className="credits__table">
                            <thead>
                                <tr>
                                    <th>{__('Servizio', 'soundbetween')}</th>
                                    <th>{__('Unità', 'soundbetween')}</th>
                                    <th>{__('Costo €', 'soundbetween')}</th>
                                    <th>{__('Ricarico %', 'soundbetween')}</th>
                                    <th>{__('Prezzo cr/unità', 'soundbetween')}</th>
                                    <th>{__('Attivo', 'soundbetween')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pricing.map(row => (
                                    <tr key={row.serviceKey}>
                                        <td>
                                            <div className="credits__svcName">{row.label}</div>
                                            <div className="credits__svcKey">{row.serviceKey}</div>
                                        </td>
                                        <td>{row.unit}</td>
                                        <td>
                                            <input
                                                type="number" step="0.001" min="0"
                                                className="credits__input"
                                                value={row.providerCost}
                                                onChange={e => handlePricingChange(row.serviceKey, 'providerCost', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number" step="1" min="0"
                                                className="credits__input"
                                                placeholder="50"
                                                value={row.markupPct ?? ''}
                                                onChange={e => handlePricingChange(row.serviceKey, 'markupPct', e.target.value)}
                                            />
                                        </td>
                                        <td className="credits__price">{row.pricePerUnitCredits}</td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={!!row.active}
                                                onChange={e => handlePricingChange(row.serviceKey, 'active', e.target.checked)}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="credits__save"
                                                disabled={savingKey === row.serviceKey}
                                                onClick={() => handleSaveRow(row)}>
                                                {savingKey === row.serviceKey ? '…' : __('Salva', 'soundbetween')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ProfileCredits;
